// fetchWithAuth.js

// Helper function for exponential backoff retry logic
async function retryWithBackoff(fn, retries = 3, delay = 1000, backoff = 2) {
  try {
    return await fn();
  } catch (error) {
    if (retries === 0 || error.status !== 429) {
      throw error;
    }
    
    await new Promise(resolve => setTimeout(resolve, delay));
    return retryWithBackoff(fn, retries - 1, delay * backoff, backoff);
  }
}

// This function must be used inside a React component or hook
export async function fetchWithAuth(url, options = {}, logout) {
  const makeRequest = async () => {
    const token = localStorage.getItem('token');
    const headers = {
      ...(options.headers || {}),
      'Authorization': `Bearer ${token}`,
    };
    
    try {
      const response = await fetch(url, { ...options, headers });
      
      if (response.status === 401 || response.status === 403) {
        // Token invalid or expired
        if (logout) logout();
        const error = new Error('Session expired. Please log in again.');
        error.status = response.status;
        throw error;
      }
      
      if (response.status === 429) {
        const error = new Error('Rate limit exceeded. Please try again later.');
        error.status = 429;
        error.response = response;
        throw error;
      }
      
      return response;
    } catch (error) {
      // If it's already a custom error with status, just rethrow it
      if (error.status) {
        throw error;
      }
      
      // For network errors or other fetch failures
      const enhancedError = new Error(error.message || 'Network request failed');
      enhancedError.originalError = error;
      throw enhancedError;
    }
  };
  
  return retryWithBackoff(makeRequest);
}